import React, { createContext, useEffect, useMemo, useState } from "react";
import {
  isSchoolDomain as isSchoolDomainFunction,
  isBiblioDomain as isLibraryDomainFunction,
  isMobileOperatorDomain as isMobileOperatorDomainFunction,
  isSubscriptionDomain as isSubscriptionDomainFunction,
  setCookie,
  isForeignDomain,
} from "@litres/utils";

import { api, useAppDispatch, useAppSelector } from "@litres-core/store";
import { getTrackerConfig } from "@litres-libs/tracker-manager";
import { useSafemode } from "@litres-modules/safemode";
import { CaptchaProvider } from "@litres/auth/captcha";
import { useSyncAnonCookies } from "./hooks/useSyncAnonCookies";
import { HeaderMobile } from "./components/HeaderMobile";
import { HeaderDesktop } from "./components/HeaderDesktop";
import { trackerStart } from "./utils/analytics";
import { useTrackAdvCakeReferral } from "./hooks/useTrackAdvCakeReferral";

interface IHeaderProps {
  pathname?: string;
}

export interface IHeaderContext {
  isSchoolDomain: boolean;
  isLibraryDomain: boolean;
  isMobileOperatorDomain: boolean;
  isSubscriptionDomain: boolean;
  isDesktop: boolean;
  isMainDomain: boolean;
  isForeignDomain: boolean;
  isPdaDomain: boolean;
}

export const HeaderContext = createContext<IHeaderContext>({
  isSchoolDomain: false,
  isLibraryDomain: false,
  isMobileOperatorDomain: false,
  isSubscriptionDomain: false,
  isDesktop: true,
  isMainDomain: true,
  isForeignDomain: false,
  // для старого стека
  isPdaDomain: false,
});

export const MAIN_DOMAIN = "www.litres.ru";
export const PDA_DOMAIN = "pda.litres.ru";

const Header = ({ pathname }: IHeaderProps) => {
  const dispatch = useAppDispatch();
  const foundationUser = useAppSelector(state => state.foundationUser);
  const browser = useAppSelector(state => state.browser);

  const [isFirstRender, setFirstRender] = useState<boolean>(true);

  const clientHost = browser.headers["client-host"] || MAIN_DOMAIN;

  useEffect(() => {
    if (isFirstRender) {
      const url = new URL(window.location.href);
      if (url.searchParams.has("need_auth")) {
        if (!foundationUser.id && url.searchParams.get("need_auth") === "1") {
          dispatch(
            api.global.interfaceRequests.setLoginPopupError({ isOpen: true })
          );
        }
        url.searchParams.delete("need_auth");
        window.history.pushState("", document.title, url.toString());
      }
      if (url.searchParams.has("new_auth")) {
        if (url.searchParams.get("new_auth") === "1") {
          setCookie("new_auth", "1");
        }
        url.searchParams.delete("new_auth");
        window.history.pushState("", document.title, url.toString());
      }
      if (url.searchParams.has("new_socials")) {
        if (url.searchParams.get("new_socials") === "1") {
          setCookie("new_socials", "1");
        }
        url.searchParams.delete("new_socials");
        window.history.pushState("", document.title, url.toString());
      }
      trackerStart(getTrackerConfig({ foundationUser, browser }));
      setFirstRender(false);
    }
  }, [isFirstRender]);

  const contextValue = useMemo(() => {
    return {
      isSchoolDomain: isSchoolDomainFunction(clientHost),
      isLibraryDomain: isLibraryDomainFunction(clientHost),
      isMobileOperatorDomain: isMobileOperatorDomainFunction(clientHost),
      isSubscriptionDomain: isSubscriptionDomainFunction(clientHost),
      isDesktop: !browser.pda,
      isMainDomain: clientHost === MAIN_DOMAIN,
      isPdaDomain: clientHost === PDA_DOMAIN,
      isForeignDomain: isForeignDomain(clientHost),
    };
  }, [clientHost]);
  // Синхронизация safemode
  useSafemode();

  /**
   * Трекаем реферальные ссылки Adv.Cake на обоих стеках
   */
  useTrackAdvCakeReferral();

  /** Арты в корзине и отложенных для анонима, синхронизация между вкладками */
  useSyncAnonCookies();

  return (
    <CaptchaProvider>
      <HeaderContext.Provider value={contextValue}>
        {browser.pda ? <HeaderMobile /> : <HeaderDesktop pathname={pathname} />}
      </HeaderContext.Provider>
    </CaptchaProvider>
  );
};

export default Header;
